<template>
  <form-section
    :title="$t('ProjectSettings_EngagementCategories_Settings_Title')"
    class="outer-form"
  >
    <el-row>
      <el-col :span="24">
        <el-row>
          <el-col :span="6">
            <rest-form-item
              :label="$t('Engagement_Category_Title')"
              prop="title"
            >
              <el-input
                :disabled="readOnly"
                v-model="model.title"
                data-qa="engagement-category-name-input"
              />
            </rest-form-item>
          </el-col>
          <el-col :span="6">
            <segment-select
              prop="segment_id"
              v-model="model.segment_id"
              :label="$t('Engagement_Category_Segments')"
              :placeholder="$t('Category_Segments_Placeholder')"
              clearable
              :tooltip="$t('Engagement_Category_Segments_Hint')"
              :read-only="readOnly"
            />
          </el-col>
          <el-col :span="6">
            <rest-form-item
              :label="$t('Engagement_Category_National_Exclusion_Check')"
              prop="national_exclusion_check"
              v-if="
                model.use_national_exclusion && isNationalExclusionAvailable
              "
            >
              <grouped-prefix-select
                :value="model.national_exclusion_check"
                v-model="model.national_exclusion_check"
                :groups="availableExclusionTypeGroups"
                :disabled="readOnly"
                :number="true"
              />
            </rest-form-item>
          </el-col>
        </el-row>
      </el-col>
    </el-row>
    <el-row>
      <el-col :span="24">
        <el-row>
          <el-col :span="4">
            <rest-form-item prop="ignore_subscription">
              <el-switch
                :disabled="readOnly"
                v-model="model.ignore_subscription"
                data-qa="ignore-subscription-switch"
              />
              <template #label>
                {{ $t('Engagement_Category_Ignore_Subscription') }}
                <form-item-i-tooltip
                  :tooltip-content="
                    $t('Engagement_Category_Ignore_Subscription_Hint')
                  "
                />
              </template>
            </rest-form-item>
          </el-col>
          <el-col :span="4" v-if="isNationalExclusionAvailable">
            <rest-form-item
              :label="$t('Engagement_Category_Use_National_Exclusion')"
              prop="use_national_exclusion"
            >
              <el-switch
                :disabled="readOnly"
                v-model="model.use_national_exclusion"
                data-qa="use-national-exclusion-switch"
              />
            </rest-form-item>
          </el-col>
          <el-col :span="4">
            <rest-form-item prop="collapse">
              <el-switch
                :disabled="readOnly"
                v-model="model.collapse"
                data-qa="no-collapsing-switch"
              />
              <template #label>
                {{ $t('Engagement_Category_Collapse') }}
                <form-item-i-tooltip
                  :tooltip-content="$t('Engagement_Category_Collapse_Hint')"
                />
              </template>
            </rest-form-item>
          </el-col>
        </el-row>
      </el-col>
    </el-row>
  </form-section>
</template>

<script>
import FormSection from '@/common/components/form/FormSection.vue'
import RestFormItem from '@/common/components/rest/RestFormItem'
import {
  nationalExclusionTypeLabels,
  nationalExclusionTypes
} from '@/common/enum/CategoryEnums'
import FormItemITooltip from '@/common/components/form/FormItemITooltip.vue'
import GroupedPrefixSelect from '@/common/components/form/GroupedPrefixSelect.vue'
import SegmentSelect from '@/common/components/segment/SegmentSelect.vue'

export default {
  name: 'EngagementCategoriesSettingsSection',
  components: {
    SegmentSelect,
    GroupedPrefixSelect,
    RestFormItem,
    FormSection,
    FormItemITooltip
  },
  props: {
    model: {
      type: Object,
      required: true
    },
    isNationalExclusionAvailable: {
      type: Boolean,
      required: true
    },
    isComeOnEnabled: {
      type: Boolean,
      required: true
    },
    isBetssonEnabled: {
      type: Boolean,
      required: true
    },
    isOasisEnabled: {
      type: Boolean,
      required: true
    },
    readOnly: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      nationalExclusionTypes,
      nationalExclusionTypeLabels,
      loadingSegments: false,
      segments: {}
    }
  },
  computed: {
    availableExclusionTypeGroups() {
      const groups = []
      if (this.isComeOnEnabled) {
        groups.push(this.comeOnExclusionGroup)
      }
      if (this.isBetssonEnabled) {
        groups.push(this.techssonExclusionGroup)
      }
      if (this.isOasisEnabled) {
        groups.push(this.oasisExclusionGroup)
      }
      return groups
    },
    comeOnExclusionGroup() {
      let comeon = {}
      comeon[nationalExclusionTypes.general_comeon] = this.getOptionName(
        nationalExclusionTypes.general_comeon
      )
      return {
        groupDesc: this.getOptionGroupDesc('comeon'),
        groupTitle: this.getOptionGroupName('comeon'),
        options: comeon
      }
    },
    techssonExclusionGroup() {
      let techsson = {}
      techsson[nationalExclusionTypes.marketing] = this.getOptionName(
        nationalExclusionTypes.marketing
      )
      techsson[nationalExclusionTypes.service] = this.getOptionName(
        nationalExclusionTypes.service
      )
      techsson[nationalExclusionTypes.general] = this.getOptionName(
        nationalExclusionTypes.general
      )

      return {
        groupDesc: this.getOptionGroupDesc('techsson'),
        groupTitle: this.getOptionGroupName('techsson'),
        options: techsson
      }
    },
    oasisExclusionGroup() {
      let oasis = {}
      oasis[nationalExclusionTypes.marketing_oasis] = this.getOptionName(
        nationalExclusionTypes.marketing_oasis
      )
      oasis[nationalExclusionTypes.marketing_bonus_oasis] = this.getOptionName(
        nationalExclusionTypes.marketing_bonus_oasis
      )

      return {
        groupDesc: this.getOptionGroupDesc('oasis'),
        groupTitle: this.getOptionGroupName('oasis'),
        options: oasis
      }
    }
  },
  methods: {
    getOptionGroupDesc(option) {
      switch (option) {
        case 'comeon':
          return this.$t(
            'Engagement_Category_National_Exclusion_GroupComeOnDesc'
          )
        case 'oasis':
          return this.$t(
            'Engagement_Category_National_Exclusion_GroupOasisDesc'
          )
        default:
          return this.$t(
            'Engagement_Category_National_Exclusion_GroupTechssonDesc'
          )
      }
    },
    getOptionGroupName(option) {
      switch (option) {
        case 'comeon':
          return this.$t('Engagement_Category_National_Exclusion_GroupComeOn')
        case 'oasis':
          return this.$t('Engagement_Category_National_Exclusion_GroupOasis')
        default:
          return this.$t('Engagement_Category_National_Exclusion_GroupTechsson')
      }
    },
    getOptionName(option) {
      return nationalExclusionTypeLabels[option]
    }
  }
}
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/modules/campaigns/components/EngagementCategoriesSettingsSection.vue